import { useClippingPlanes } from "@/hooks/use-clipping-planes";
import { ActiveToolAndPicking, PickingToolsRef } from "@/tools/picking-tools";
import { PointerMovePreviewHandlerRef } from "@/tools/pointer-move-preview-handler";
import { usePickingToolsCallbacks } from "@/tools/use-picking-tools-callbacks";
import { assert, GUID, TypedEvent } from "@faro-lotv/foundation";
import { ThreeEvent } from "@react-three/fiber";
import { DomEvent } from "@react-three/fiber/dist/declarations/src/core/events";
import {
  createContext,
  JSX,
  PropsWithChildren,
  RefObject,
  useContext,
  useMemo,
  useState,
} from "react";
import { Plane, Vector3 } from "three";
import { GuidedNavigationState } from "./guided-navigation/guided-navigation-state";

/**
 * This type contains information that should be shared among multiple components of the flywalk mode.
 * Such information is useful to connect components to tools functionalities, such as clipping planes or
 * callbacks that raycast on models.
 */
export type ToolsAcquaintance = {
  /** The stored clipping planes */
  clippingPlanes: Plane[];
  /** The function to modify the clipping planes */
  setClippingPlanes(p: Plane[]): void;
  /** A reference to the PickingTools component */
  tools: RefObject<PickingToolsRef | null>;
  /** Pointer event handler that forwards the events to the picking tools reference */
  onModelHovered(ev: ThreeEvent<DomEvent>, iElementId: GUID): void;
  /** Pointer event handler that forwards the events to the picking tools reference */
  onModelClicked(ev: ThreeEvent<MouseEvent>, iElementId: GUID): void;
  /** Pointer event handler that forwards the events to the picking tools reference */
  onModelZoomed(ev: ThreeEvent<MouseEvent>, iElementId: GUID): void;
  /** A reference to the pointer move preview component */
  pointerMovePreview: RefObject<PointerMovePreviewHandlerRef | null>;
  /** Whether the clipping box is being edited */
  clippingBoxChanging: boolean;
  /** Set whether the clipping box is being edited */
  setClippingBoxChanging(changing: boolean): void;
  /** The current active tool and whether the tool requires picking */
  activeToolAndPicking: ActiveToolAndPicking;
  /** Set the current active tool and whether the tool requires picking */
  setActiveToolAndPicking(p: ActiveToolAndPicking): void;
  /** Whether a waypoint is being hovered by the mouse */
  isWaypointHovered: boolean;
  /** Sets whether a waypoint is being hovered */
  setWaypointHovered(h: boolean): void;
  /** Event triggered when the camera stops translating */
  onCameraStoppedTranslating: TypedEvent<void>;
  /** Whether the main object is rendered with a supporting point cloud for 3D measurements and annotations */
  hasPointCloudBehind: boolean;
  /** Sets whether the main object is rendered with a supporting point cloud for 3D measurements and annotations */
  setHasPointCloudBehind(p: boolean): void;
  /** The point currently being hovered by the active picking tool */
  pickingCurrentPoint: Vector3;
  /** Signal triggered when the user clicks the zoom lens with a valid 'green' target */
  onValidClickInZoomLens: TypedEvent<GUID>;
  /** State of the guided navigation through Gaussian Splats */
  guidedNavigation: GuidedNavigationState;
  /** Whether the guided navigation through Gaussian Splats is active */
  isGuidedNavigationActive: boolean;
  /** Sets whether the guided navigation through Gaussian Splats is active */
  setGuidedNavigationActive(active: boolean): void;
};

/**
 * This context contains information that should be shared among multiple components of the flywalk mode.
 */
const ToolsAcquaintanceContext = createContext<ToolsAcquaintance | undefined>(
  undefined,
);

/** @returns the ToolsAcquaintance context */
export function useToolsAcquaintance(): ToolsAcquaintance {
  const ctx = useContext(ToolsAcquaintanceContext);
  assert(ctx, "Tools Acquaintance context should be initialized");
  return ctx;
}

/** @returns A component to create the ToolsAcquaintance context */
export function ToolsAcquaintanceContextProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const { clippingPlanes, setClippingPlanes } = useClippingPlanes();

  const {
    tools,
    onModelHovered,
    onModelClicked,
    onModelZoomed,
    pointerMovePreview,
  } = usePickingToolsCallbacks();

  const [clippingBoxChanging, setClippingBoxChanging] = useState(false);

  const [activeToolAndPicking, setActiveToolAndPicking] =
    useState<ActiveToolAndPicking>({
      activeTool: null,
      isPickingToolActive: false,
    });

  const [isWaypointHovered, setWaypointHovered] = useState(false);

  const onCameraStoppedTranslating = useMemo(() => new TypedEvent<void>(), []);

  const [hasPointCloudBehind, setHasPointCloudBehind] = useState(false);

  const [pickingCurrentPoint] = useState(new Vector3());
  const onValidClickInZoomLens = useMemo(() => new TypedEvent<GUID>(), []);

  const [guidedNavigation] = useState(() => new GuidedNavigationState());
  const [isGuidedNavigationActive, setGuidedNavigationActive] = useState(false);

  const value = useMemo(
    () => ({
      clippingPlanes,
      setClippingPlanes,
      tools,
      onModelHovered,
      onModelClicked,
      onModelZoomed,
      pointerMovePreview,
      clippingBoxChanging,
      setClippingBoxChanging,
      activeToolAndPicking,
      setActiveToolAndPicking,
      isWaypointHovered,
      setWaypointHovered,
      onCameraStoppedTranslating,
      hasPointCloudBehind,
      setHasPointCloudBehind,
      pickingCurrentPoint,
      onValidClickInZoomLens,
      guidedNavigation,
      isGuidedNavigationActive,
      setGuidedNavigationActive,
    }),
    [
      clippingPlanes,
      setClippingPlanes,
      tools,
      onModelHovered,
      onModelClicked,
      onModelZoomed,
      pointerMovePreview,
      clippingBoxChanging,
      setClippingBoxChanging,
      activeToolAndPicking,
      setActiveToolAndPicking,
      isWaypointHovered,
      setWaypointHovered,
      onCameraStoppedTranslating,
      hasPointCloudBehind,
      setHasPointCloudBehind,
      pickingCurrentPoint,
      onValidClickInZoomLens,
      guidedNavigation,
      isGuidedNavigationActive,
    ],
  );

  return (
    <ToolsAcquaintanceContext value={value}>
      {children}
    </ToolsAcquaintanceContext>
  );
}
